import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Loader2,
  CreditCard,
  ShieldCheck,
  AlertTriangle,
  RotateCcw,
  LogOut,
} from "lucide-react";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCardFundingBlock } from "@/hooks/useCardFundingBlock";
import { CREDIT_CARD_ONLY_MESSAGE } from "@/lib/stripe/credit-card";
import { isStripePublishableKeyConfigured } from "@/lib/stripe/env";
import { getStripePromise } from "@/lib/stripe/stripe";
import {
  classifySupabaseFunctionError,
  isUsableErrorMessage,
  mapStripeConfirmError,
  paymentErrorMessage,
} from "@/lib/stripe/payment-errors";
import { reportPaymentError } from "@/lib/stripe/telemetry";
import { IS_DEV } from "@/lib/environment";
import { trackEcommerce, trackEvent } from "@/lib/dataLayer";

/**
 * CardSetupModal — Mandato de tarjeta en el REGISTRO (Fase 2 del onboarding).
 *
 * Guarda el método de pago del usuario para cobros a posteriori SIN retener
 * ni congelar dinero, usando el combo Customer + SetupIntent + off_session:
 *   1. Edge Function `create-payment-setup` asegura el Customer y crea un
 *      SetupIntent `usage: 'off_session'`.
 *   2. Este modal muestra un PaymentElement de SOLO tarjeta junto con la
 *      cláusula de autorización legal (mandato).
 *   3. `stripe.confirmSetup` valida la tarjeta con SCA/3DS (on-session).
 *   4. El webhook `setup_intent.succeeded` verifica que sea una tarjeta válida
 *      (débito o crédito bancaria) y, solo entonces, la guarda como default del Customer
 *      (`users.stripe_payment_method_id`) para los cobros off-session.
 *      Las tarjetas prepago se detachan y rechazan.
 *
 * Además del mandato legal, aquí se bloquean tarjetas de prepago en el
 * frontend vía `carddetailschange` en PRODUCCIÓN. En DESARROLLO, la validación
 * se deshabilita para permitir cualquier tarjeta de test de Stripe.
 * El servidor valida igualmente en PRODUCCIÓN via webhook.
 */
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "";
// Solo inicializa Stripe si la clave está realmente configurada (detecta
// placeholders como pk_test_REEMPLAZAR_...); sin clave válida el modal muestra
// un error claro en vez de montar un PaymentElement que fallaría con 401.
const stripeKeyOk = isStripePublishableKeyConfigured(STRIPE_PUBLISHABLE_KEY);

const MANDATE_TEXT =
  "Al registrarte, autorizas a “Brickshare.org - Infinigameslab SL” a conservar tu método de pago y a realizar cargos posteriores en caso de incumplimiento de los Términos de Servicio, daños, pérdida o no devolución de los activos alquilados.";

type Phase = "loading" | "ready" | "success" | "error";

// Resultado del polling de confirmación del webhook (handleSetupDone).
// `error` permite al formulario mostrar un mensaje específico
// (p. ej. tarjeta prepago rechazada) en vez del genérico.
interface SetupDoneResult {
  ok: boolean;
  error?: string;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// ✅ Singleton de Stripe Promise (no se recrea durante HMR).
// getStripePromise nunca rechaza: si js.stripe.com no carga resuelve a null
// y resetea su caché interna para que "Reintentar" reintente la carga.

interface CardSetupModalProps {
  open: boolean;
}

export default function CardSetupModal({ open }: CardSetupModalProps) {
  const { refreshProfile, signOut, deleteUserAccount } = useAuth();
  const [phase, setPhase] = useState<Phase>("loading");
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [showAbortConfirm, setShowAbortConfirm] = useState(false);
  const [isAborting, setIsAborting] = useState(false);
  const startedRef = useRef(false);

  // ✅ Usar singleton de Stripe Promise (lib) en lugar de useMemo
  // Esto evita que el prop stripe cambie durante HMR
  const stripePromise = getStripePromise(STRIPE_PUBLISHABLE_KEY);

  const preventClose = useCallback((e: Event) => {
    // Prevenir cerrar durante fases críticas (loading, ready, processing)
    if (phase === "loading" || phase === "ready") {
      e.preventDefault();
    }
  }, [phase]);

  // Interceptar click en X para mostrar confirmación
  const handleCloseAttempt = useCallback((isOpen: boolean) => {
    if (!isOpen && !isAborting) {
      setShowAbortConfirm(true);
    }
  }, [isAborting]);

  // Ejecutar abort: delete account + logout + redirect
  const handleAbortProcess = useCallback(async () => {
    setIsAborting(true);
    try {
      const { error } = await deleteUserAccount();
      if (error) {
        console.error("[CardSetupModal] Error deleting account:", error);
      }
      await signOut();
      window.location.href = "https://brickshare.org";
    } catch (err) {
      console.error("[CardSetupModal] Abort process failed:", err);
      setIsAborting(false);
    }
  }, [deleteUserAccount, signOut]);

  const loadSetup = useCallback(async () => {
    setPhase("loading");
    setErrorMsg(null);
    // Sin publishable key válida no tiene sentido crear el SetupIntent:
    // el PaymentElement fallaría al cargar la sesión (401 en stripe.js).
    if (!stripeKeyOk) {
      setPhase("error");
      setErrorMsg(
        "Falta la clave pública de Stripe (VITE_STRIPE_PUBLISHABLE_KEY). Configúrala en apps/web/.env.local y reinicia el servidor de desarrollo."
      );
      return;
    }
    try {
      console.log("[CardSetupModal.loadSetup] Solicitando SetupIntent...");
      
      // Asegurar que la sesión (token JWT) no ha caducado antes de llamar a la Edge Function (mitiga 401 Unauthorized)
      await supabase.auth.getSession();
      
      const { data, error } = await supabase.functions.invoke("create-payment-setup", {});
      
      console.log("[CardSetupModal.loadSetup] Response:", {
        error: !!error,
        success: data?.success,
        already_saved: data?.already_saved,
        has_client_secret: !!data?.client_secret,
      });

      if (error) {
        console.error("[CardSetupModal.loadSetup] Function error:", error.message);
        
        // Enviar contexto extra a Sentry para destapar errores 400/500 disfrazados de FunctionsHttpError
        const errorDetails = error as any;
        reportPaymentError("create_setup_invoke", error, {
          backendMessage: data?.error || classifySupabaseFunctionError(error, data),
          status: errorDetails?.context?.status || errorDetails?.status
        });
        
        setPhase("error");
        setErrorMsg(classifySupabaseFunctionError(error, data));
        return;
      }

      if (data?.already_saved) {
        console.log("[CardSetupModal.loadSetup] Tarjeta ya guardada en BD");
        // Ya tenía tarjeta guardada; refrescamos perfil y el gate se cierra.
        await refreshProfile?.();
        setPhase("success");
        return;
      }

      if (!data?.client_secret) {
        console.error("[CardSetupModal.loadSetup] No client_secret recibido:", data);
        reportPaymentError("create_setup_invoke", new Error("no_client_secret"));
        setPhase("error");
        setErrorMsg((data as any)?.error ?? paymentErrorMessage("setup_init_error"));
        return;
      }

      console.log("[CardSetupModal.loadSetup] SetupIntent obtenido, preparando formulario...");
      setClientSecret(data.client_secret as string);
      setPhase("ready");
    } catch (err: any) {
      console.error("[CardSetupModal.loadSetup] Exception:", err);
      reportPaymentError("create_setup_invoke", err);
      setPhase("error");
      setErrorMsg(paymentErrorMessage("setup_init_error"));
    }
  }, [refreshProfile]);

  // Safety net: el PaymentElement (o stripe.js) no termina de cargar.
  // Mensaje accionable (conexión/adblock/otro navegador) desde el mapa
  // único de errores de pago.
  const handleStripeUnavailable = useCallback(() => {
    setPhase("error");
    setErrorMsg(paymentErrorMessage("stripe_js_load"));
  }, []);

  // Safety net: si seguimos en "ready" pero la instancia de stripe nunca
  // cargó (ej. sin red), error en vez de hang eterno.
  useEffect(() => {
    if (phase !== "ready") return;
    let stripeLoaded: boolean | null = null;
    stripePromise.then((s) => {
      stripeLoaded = !!s;
    });
    const t = setTimeout(() => {
      if (stripeLoaded === false) {
        reportPaymentError("element_timeout", new Error("stripe_load_watchdog_12s"));
        handleStripeUnavailable();
      }
    }, 12000);
    return () => clearTimeout(t);
  }, [phase, stripePromise, handleStripeUnavailable]);

  // Tras confirmSetup correcto: esperar al webhook setup_intent.succeeded que
  // guarda `users.stripe_payment_method_id`, mientras mostramos confirmación.
  const handleSetupDone = useCallback(async (setupIntentId?: string): Promise<SetupDoneResult> => {
    console.log("[CardSetupModal] Iniciando polling para verificar webhook...", { setupIntentId });
    for (let i = 0; i < 20; i += 1) {
      if (i > 0) {
        await sleep(1500);
      }
      const { data, error } = await supabase.functions.invoke("create-payment-setup", {
        body: setupIntentId ? { setup_intent_id: setupIntentId } : {},
      });

      if (error) {
        console.warn(`[CardSetupModal] Intento ${i + 1}/20: Error temporal:`, error);
        // Continuar polling: el error puede ser temporal
        continue;
      }

      // ✅ LOG: Ver exactamente qué retorna
      console.log(`[CardSetupModal] Intento ${i + 1}/20 - Response:`, {
        success: data?.success,
        already_saved: data?.already_saved,
        customer_id: data?.customer_id,
        payment_method_id: data?.payment_method_id,
        error_if_any: data?.error,
        error_code: data?.error_code,
      });

      // Si la tarjeta fue rechazada por prepago, el formulario muestra el
      // mensaje específico (el estado setupError vive en CardSetupForm).
      if (data?.error_code === "prepaid_card_rejected") {
        return { ok: false, error: "Tu tarjeta prepago fue rechazada. Por favor, introduce una tarjeta de débito o crédito bancaria." };
      }

      if (data?.already_saved) {
        console.log("[CardSetupModal] ✅ Confirmado: tarjeta guardada");
        await refreshProfile?.();
        setPhase("success");
         // TRACKING: add_payment_info al guardar tarjeta con éxito
          trackEcommerce("add_payment_info", {
            currency: "EUR",
            value: 0, // No hay monto fijo tras cambio de modelo
            payment_type: "card",
          });
          trackEvent("bs_card_setup_complete", { payment_type: "credit_card" });
        return { ok: true };
      }


      // Log de progreso cada 5 intentos
      if (i > 0 && i % 5 === 0) {
        console.log(`[CardSetupModal] Esperando webhook... intento ${i + 1}/20`);
      }
    }

    // Timeout: dar mensaje claro + telemetría (webhook lento / ausente).
    console.error("[CardSetupModal] ❌ Timeout esperando webhook (20 intentos = 30 segundos)");
    reportPaymentError("setup_polling_timeout", new Error("setup_intent_webhook_timeout_30s"));
    await refreshProfile?.();
    return { ok: false };
  }, [refreshProfile]);

  useEffect(() => {
    if (!open) {
      startedRef.current = false;
      setClientSecret(null);
      setPhase("loading");
      setErrorMsg(null);
      return;
    }
    if (startedRef.current) return;
    startedRef.current = true;
    void loadSetup();
  }, [open, loadSetup]);

  return (
    <>
      <Dialog open={open} onOpenChange={handleCloseAttempt}>
        <DialogContent
          className="sm:max-w-[500px] flex flex-col gap-4"
          onInteractOutside={preventClose}
          onPointerDownOutside={preventClose}
        >
        {/* Header */}
        <div className="border-b pb-4 pr-8">
          <DialogHeader className="gap-2">
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-primary" />
              Guarda tu tarjeta
            </DialogTitle>
            <DialogDescription>
              Guardamos tu tarjeta de forma segura para los cobros del alquiler
              (semanales y posibles cargos por daños o no devolución). No se realiza
              ningún cobro ni retención en este paso.
            </DialogDescription>
            <div className="mt-2 rounded-lg border border-orange-300 bg-orange-50 p-3 text-sm text-orange-900">
              <strong>✅ Aceptamos débito y crédito.</strong> No se admiten tarjetas de prepago.
            </div>
          </DialogHeader>
        </div>

        {/* Body */}
        <div className="py-2">
          {phase === "loading" && (
            <div className="flex items-center justify-center gap-3 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              Preparando el guardado seguro...
            </div>
          )}

          {phase === "error" && (
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <AlertTriangle className="h-8 w-8 text-destructive" />
              <p className="text-sm text-destructive">{errorMsg}</p>
              <Button onClick={loadSetup} variant="outline" className="w-full">
                <RotateCcw className="mr-2 h-4 w-4" />
                Reintentar
              </Button>
            </div>
          )}

          {phase === "success" && (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <ShieldCheck className="h-10 w-10 text-emerald-500" />
              <p className="text-sm font-medium">Método de pago guardado</p>
              <p className="text-xs text-muted-foreground">
                Tu tarjeta de crédito queda vinculada a tu perfil para futuros cobros.
              </p>
            </div>
          )}

          {phase === "ready" && clientSecret && (
            <Elements
              key={clientSecret}
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: { theme: "stripe" },
                locale: "es",
              }}
            >
              <CardSetupForm
                clientSecret={clientSecret}
                onDone={handleSetupDone}
                onStripeUnavailable={handleStripeUnavailable}
              />
            </Elements>
          )}
        </div>
        </DialogContent>
      </Dialog>

      {/* AlertDialog de confirmación para abortar registro */}
      <AlertDialog open={showAbortConfirm} onOpenChange={setShowAbortConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Abandonar el registro?</AlertDialogTitle>
            <AlertDialogDescription>
              Si abandonas ahora, tu cuenta será eliminada y tendrás que empezar 
              el proceso de registro desde cero. ¿Estás seguro?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex gap-3">
            <AlertDialogCancel disabled={isAborting}>
              Continuar registro
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAbortProcess}
              disabled={isAborting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isAborting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Abandonando...
                </>
              ) : (
                <>
                  <LogOut className="mr-2 h-4 w-4" />
                  Sí, abandonar
                </>
              )}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>

     </>
   );
 }
interface CardSetupFormProps {
  clientSecret: string;
  onDone: (setupIntentId?: string) => Promise<SetupDoneResult>;
  onStripeUnavailable?: () => void;
}

function CardSetupForm({ clientSecret, onDone, onStripeUnavailable }: CardSetupFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { profile } = useAuth();
  const [mandateAccepted, setMandateAccepted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isElementReady, setIsElementReady] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [showMandateError, setShowMandateError] = useState(false);
  const { blocked: cardFundingBlocked, register: registerCardFunding } = useCardFundingBlock(!IS_DEV);
  const formRef = useRef<HTMLFormElement>(null);

  // Manejo de ENTER en el formulario: permite enviar presionando ENTER desde cualquier campo
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLFormElement>) => {
    // Si el usuario presiona ENTER en el formulario y no está procesando, enviar
    if (e.key === "Enter" && !isProcessing && mandateAccepted && formRef.current) {
      e.preventDefault();
      formRef.current.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
    }
  }, [isProcessing, mandateAccepted]);

  // Safety net 1: el PaymentElement no carga (clave inválida / 401 / red) →
  // evento "loaderror" de stripe.js → error visible en vez de formulario en blanco.
  useEffect(() => {
    if (!elements) return;
    const onLoadError = (err?: unknown) => {
      console.error("[CardSetupModal] PaymentElement loaderror:", err);
      reportPaymentError("element_load_error", err);
      onStripeUnavailable?.();
    };
    try {
      (elements as any).on?.("loaderror", onLoadError);
    } catch (e) {
      console.warn("[CardSetupModal] No se pudo suscribir a 'loaderror':", e);
    }
    return () => {
      try {
        (elements as any).off?.("loaderror", onLoadError);
      } catch {
        /* noop */
      }
    };
  }, [elements, onStripeUnavailable]);

  // Safety net 2: si tras 5s stripe/elements siguen sin estar disponibles
  // (p. ej. loadStripe resolvió a null por falta de red) → error visible.
  useEffect(() => {
    const t = setTimeout(() => {
      if (!stripe || !elements) {
        reportPaymentError("element_timeout", new Error("stripe_elements_not_ready_5s"));
        onStripeUnavailable?.();
      }
    }, 5000);
    return () => clearTimeout(t);
  }, [stripe, elements, onStripeUnavailable]);

  // Safety net 3: el PaymentElement montado nunca llega a ready (error
  // interno de stripe.js, iframe matado por el OS, red 3G) → error visible
  // en vez de "Cargando formulario de pago seguro…" para siempre.
  useEffect(() => {
    if (isElementReady) return;
    const t = setTimeout(() => {
      console.error("[CardSetupModal] PaymentElement no ready tras 15s");
      reportPaymentError("element_timeout", new Error("payment_element_not_ready_15s"));
      onStripeUnavailable?.();
    }, 15000);
    return () => clearTimeout(t);
  }, [isElementReady, onStripeUnavailable]);

  // Watchdog de confirmación: si submit/confirmSetup/3DS no resuelve en
  // 120s (redirect de banco colgado, navegador que mata el iframe) →
  // error visible con reintento, nunca un botón "Procesando…" eterno.
  const confirmWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stopConfirmWatchdog = useCallback(() => {
    if (confirmWatchdogRef.current) {
      clearTimeout(confirmWatchdogRef.current);
      confirmWatchdogRef.current = null;
    }
  }, []);
  // Limpieza al desmontar (p. ej. el modal se cierra durante confirmación).
  useEffect(() => stopConfirmWatchdog, [stopConfirmWatchdog]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!stripe || !elements) return;
      // Mandato legal: sin aceptación expresa no guardamos la tarjeta.
      if (!mandateAccepted) {
        setShowMandateError(true);
        return;
      }
      // Política "solo crédito" (UX; el servidor valida SIEMPRE vía webhook).
      if (cardFundingBlocked) {
        setSetupError(CREDIT_CARD_ONLY_MESSAGE);
        return;
      }
      setIsProcessing(true);
      setSetupError(null);
      setShowMandateError(false);

      // Watchdog: la confirmación (submit/3DS/SCA) debe resolver en <120s.
      stopConfirmWatchdog();
      confirmWatchdogRef.current = setTimeout(() => {
        console.error("[CardSetupModal] Watchdog de confirmación agotado (120s)");
        reportPaymentError("confirm_timeout", new Error("confirm_setup_watchdog_120s"));
        setSetupError(paymentErrorMessage("confirm_timeout"));
        setIsProcessing(false);
      }, 120_000);

      try {
        // Stripe.js v3+ requiere elements.submit() antes de confirmSetup.
        const submitResult = await elements.submit();
        if (submitResult.error) {
          stopConfirmWatchdog();
          setSetupError(submitResult.error.message ?? paymentErrorMessage("generic"));
          setIsProcessing(false);
          return;
        }

        const { error, setupIntent } = await stripe.confirmSetup({
          elements,
          clientSecret,
          redirect: "if_required",
          confirmParams: {
            payment_method_data: {
              billing_details: {
                name: profile?.full_name || undefined,
                email: profile?.email || undefined,
              },
            },
            return_url: `${window.location.origin}/dashboard`,
          },
        });

        if (error) {
          stopConfirmWatchdog();
          reportPaymentError("confirm_exception", error, { code: error.code });
          setSetupError(
            isUsableErrorMessage(error.message)
              ? error.message
              : paymentErrorMessage(mapStripeConfirmError(error)),
          );
          setIsProcessing(false);
          return;
        }

        if (setupIntent?.status === "requires_action") {
          const { error: nextError } = await stripe.handleNextAction({ clientSecret });
          if (nextError) {
            stopConfirmWatchdog();
            reportPaymentError("confirm_exception", nextError, { code: nextError.code });
            setSetupError(
              isUsableErrorMessage(nextError.message)
                ? nextError.message
                : paymentErrorMessage("confirm_exception"),
            );
            setIsProcessing(false);
            return;
          }
        }

        // La confirmación resolvió; lo que queda es esperar al webhook
        // (handleSetupDone tiene su propio timeout de 30s con mensaje).
        stopConfirmWatchdog();
        const result = await onDone(setupIntent?.id);
        setIsProcessing(false);
        if (!result.ok) {
          setSetupError(result.error ?? paymentErrorMessage("setup_confirm_timeout"));
        }
      } catch (err) {
        // stripe.js lanzando excepción (error interno en el navegador,
        // iframe roto, red intermitente): nunca dejar "Procesando…" atascado.
        console.error("[CardSetupModal] Excepción en confirmación:", err);
        stopConfirmWatchdog();
        reportPaymentError("confirm_exception", err);
        setSetupError(paymentErrorMessage("confirm_exception"));
        setIsProcessing(false);
      }
    },
    [stripe, elements, clientSecret, mandateAccepted, cardFundingBlocked, profile, onDone, stopConfirmWatchdog],
  );

  return (
    <form 
      ref={formRef}
      onSubmit={handleSubmit}
      onKeyDown={handleKeyDown}
      className="flex flex-col gap-4"
    >
      {/* Content */}
      <div className="space-y-5">
        <div className="relative min-h-[160px]">
          {!isElementReady && (
            <div className="absolute inset-0 flex items-center justify-center gap-2 text-sm text-muted-foreground bg-background/80 z-10">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <span>Cargando formulario de pago seguro...</span>
            </div>
          )}
          <PaymentElement
            options={{
              layout: "accordion",
              paymentMethodOrder: ["card"],
              wallets: {
                applePay: "never",
                googlePay: "never",
                // Link: nunca en el mandato de registro.
                link: "never" as never,
              },
              defaultValues: {
                billingDetails: {
                  name: profile?.full_name || "",
                  email: profile?.email || "",
                },
              },
            }}
            onReady={(el) => {
              setIsElementReady(true);
              registerCardFunding(el);
            }}
            onLoaderStart={() => {
              // Loader started
            }}
            onLoadError={(err) => {
              console.error("[CardSetupModal] PaymentElement onLoadError prop:", err);
              reportPaymentError("element_load_error", err);
              onStripeUnavailable?.();
            }}
          />
        </div>

        {cardFundingBlocked && (
          <div
            data-testid="card-setup-credit-only-warning"
            role="alert"
            className="border-l-4 border-red-500 bg-red-50 p-4 rounded-md"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-semibold text-red-800">
                {CREDIT_CARD_ONLY_MESSAGE}
              </p>
            </div>
          </div>
        )}

        <div className="rounded-xl border border-orange-200 bg-orange-50 p-4">
          <label htmlFor="card-mandate" className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              id="card-mandate"
              checked={mandateAccepted}
              onCheckedChange={(v) => {
                setMandateAccepted(v === true);
                setShowMandateError(false);
              }}
              className="mt-0.5"
              data-testid="card-mandate-checkbox"
            />
            <span className="text-xs leading-relaxed text-foreground">
              <span className="font-semibold">Autorización de cobros posteriores.</span>{" "}
              {MANDATE_TEXT}
            </span>
          </label>
        </div>

        {showMandateError && (
          <p className="text-xs text-destructive">Debes aceptar la autorización para continuar.</p>
        )}
        {setupError && (
          <div
            data-testid="card-setup-error"
            role="alert"
            className="border-l-4 border-red-500 bg-red-50 p-4 rounded-md"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">
                {setupError}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Button */}
      <div className="border-t pt-4">
        <Button
          type="submit"
          disabled={!stripe || isProcessing || cardFundingBlocked}
          className="w-full bg-primary"
          data-testid="card-setup-submit"
        >
          {isProcessing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Procesando...
            </>
          ) : (
            "Guardar tarjeta"
          )}
        </Button>
      </div>
    </form>
  );
}